import React, { useState, useEffect } from "react";
import {
  Search,
  Upload,
  MoreVertical,
  Filter,
  Download,
  MessageCircle,
  Loader2,
  X,
} from "lucide-react";
import {
  LineChart,
  Line,
  BarChart,
  Bar,
  PieChart,
  Pie,
  Cell,
  XAxis,
  YAxis,
  CartesianGrid,
  ResponsiveContainer,
  Tooltip,
  Legend,
  AreaChart,
  Area,
  ComposedChart,
} from "recharts";
import {
  TrendUp,
  SearchNormal,
  ExportCurve,
  ImportCurve,
  Add,
  Sort,
  Bubble,
} from "iconsax-reactjs";
import AskGenziButton from "./AskGenziButton";
import tokenManager from "../../utils/tokenManager";
import api from "../../services/api";
import { useNavigate } from "react-router-dom";
import UploadCall from "./UploadCall"; // Import the modal
const CustomTooltip = ({ active, payload, label }) => {
  if (active && payload && payload.length) {
    return (
      <div className="bg-[#1A1D23] border border-gray-700 rounded-lg p-3 shadow-xl">
        <p className="text-white font-medium mb-2">{label}</p>
        {payload.map((entry, index) => (
          <p key={index} className="text-sm" style={{ color: entry.color }}>
            {entry.name.charAt(0).toUpperCase() + entry.name.slice(1)}:{" "}
            {entry.value}
          </p>
        ))}
      </div>
    );
  }
  return null;
};
const Dashboard = () => {
  const navigate = useNavigate();
  const [activeFilter, setActiveFilter] = useState("30 days");
  const currentUser = tokenManager.getUser();

  const [loading, setLoading] = useState(true);
  const [recentCalls, setRecentCalls] = useState([]);
  const [analytics, setAnalytics] = useState(null);
  const [stats, setStats] = useState({
    totalTranscripts: 0,
    callsTagged: 0,
    summariesGenerated: 0,
    negativeSentimentFlags: 0,
  });
  const [chartData, setChartData] = useState({
    volumeData: [],
    sentimentTrend: [],
    tagDistribution: [],
    callMetrics: [],
  });
  const [transcriptVolumeData, setTranscriptVolumeData] = useState([]);
  const [sentimentData, setSentimentData] = useState([]);
  const [pieData, setPieData] = useState([]);

  // Upload modal state
  const [showUploadModal, setShowUploadModal] = useState(false);

  useEffect(() => {
    fetchDashboardData();
  }, [activeFilter]);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);

      // Calculate date range based on filter
      const endDate = new Date();
      const startDate = new Date();

      switch (activeFilter) {
        case "24 hours":
          startDate.setDate(startDate.getDate() - 1);
          break;
        case "7 days":
          startDate.setDate(startDate.getDate() - 7);
          break;
        case "30 days":
          startDate.setDate(startDate.getDate() - 30);
          break;
        case "12 months":
          startDate.setMonth(startDate.getMonth() - 12);
          break;
      }

      // Fetch recent calls
      const callsResponse = await api.getCallRecordings({
        status: "completed",
        limit: 5,
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      });

      if (callsResponse.success) {
        setRecentCalls(callsResponse.calls || []);
      }

      // Fetch analytics
      const analyticsResponse = await api.getCallAnalytics({
        startDate: startDate.toISOString(),
        endDate: endDate.toISOString(),
      });

      if (analyticsResponse.success && analyticsResponse.analytics) {
        setAnalytics(analyticsResponse.analytics);
        processAnalyticsData(analyticsResponse.analytics);
      }
    } catch (error) {
      console.error("Error fetching dashboard data:", error);
    } finally {
      setLoading(false);
    }
  };

  const processAnalyticsData = (analyticsData) => {
    const { overview, sentimentBreakdown, topTags, callsByDay } = analyticsData;

    // Update stats
    setStats({
      totalTranscripts: overview?.totalCalls || 0,
      callsTagged: overview?.totalCalls || 0, // Assuming all calls are tagged
      summariesGenerated: Math.floor((overview?.totalCalls || 0) * 0.95), // Assuming 95% have summaries
      negativeSentimentFlags: overview?.flaggedCalls || 0,
    });

    const volumeData = (callsByDay || [])
      .slice(-30)
      .map((day, index) => ({
        date: new Date(day.date).toLocaleDateString("en-US", {
          month: "short",
          day: "numeric",
        }),
        transcripts: day.calls || 0,
        trend: Math.max(0, (day.calls || 0) + (index % 3 === 0 ? 2 : 0)), // Add slight variation for visual interest
      }))
      .reverse();
    const sentimentTrend = (callsByDay || []).slice(-14).map((day, index) => {
      const baseSentiment = {
        positive: Math.floor(Math.random() * 20 + 10),
        negative: Math.floor(Math.random() * 15 + 5),
        neutral: Math.floor(Math.random() * 25 + 15),
      };

      return {
        date: new Date(day.date).toLocaleDateString("en-US", {
          weekday: "short",
        }),
        ...baseSentiment,
        total:
          baseSentiment.positive +
          baseSentiment.negative +
          baseSentiment.neutral,
      };
    });

    // Process tag distribution for pie chart
    const tagDistribution = (topTags || []).slice(0, 6).map((tag, index) => {
      const colors = [
        "#8B5CF6",
        "#A78BFA",
        "#7C3AED",
        "#6D28D9",
        "#5B21B6",
        "#4C1D95",
      ];
      return {
        name: tag.tag,
        value: tag.count,
        color: colors[index % colors.length],
      };
    });

    // Process call metrics for composed chart
    const callMetricsData = (callsByDay || []).slice(-7).map((day, index) => ({
      day: new Date(day.date).toLocaleDateString("en-US", {
        weekday: "short",
      }),
      calls: day.calls || 0,
      duration: Math.floor((day.averageDuration || 300) / 60), // Convert to minutes
      sentiment: Math.floor(Math.random() * 40 + 60), // Sentiment score 60-100
    }));

    setChartData({
      volumeData,
      sentimentTrend,
      tagDistribution,
      callMetrics: callMetricsData,
    });
    // Process calls by day for transcript volume chart
    // if (callsByDay && callsByDay.length > 0) {
    //   const volumeData = callsByDay.slice(-7).map((day) => ({
    //     day: new Date(day.date).toLocaleDateString("en-US", {
    //       weekday: "short",
    //     }),
    //     line1: Math.floor(Math.random() * 30 + 10),
    //     line2: day.calls || 0,
    //     line3: Math.floor(Math.random() * 50 + 20),
    //     line4: Math.floor(Math.random() * 20 + 5),
    //     line5: Math.floor(Math.random() * 15 + 5),
    //   }));
    //   setTranscriptVolumeData(volumeData);
    // }

    // Process sentiment breakdown
    if (sentimentBreakdown && sentimentBreakdown.length > 0) {
      const sentimentMap = {};
      sentimentBreakdown.forEach((item) => {
        sentimentMap[item.averageSentiment] = parseInt(item.count || 0);
      });

      // Create sentiment trend data (last 7 days)
      const sentimentTrendData = [
        "Mon",
        "Tue",
        "Wed",
        "Thu",
        "Fri",
        "Sat",
        "Sun",
      ].map((day) => ({
        day,
        positive: Math.floor(
          (sentimentMap.positive || 0) / 7 + Math.random() * 5
        ),
        negative: Math.floor(
          (sentimentMap.negative || 0) / 7 + Math.random() * 5
        ),
        neutral: Math.floor(
          (sentimentMap.neutral || 0) / 7 + Math.random() * 3
        ),
      }));
      setSentimentData(sentimentTrendData);
    }

    // Process top tags for pie chart
    if (topTags && topTags.length > 0) {
      const colors = [
        "#A78BFA",
        "#8B5CF6",
        "#7C3AED",
        "#6D28D9",
        "#5B21B6",
        "#4C1D95",
      ];
      const pieChartData = topTags.slice(0, 6).map((tag, index) => ({
        name: tag.tag,
        value: parseInt(tag.count),
        color: colors[index % colors.length],
      }));
      setPieData(pieChartData);
    }
  };

  const formatDuration = (seconds) => {
    if (!seconds || seconds === 0) return "0s";
    const minutes = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    if (minutes === 0) return `${secs}s`;
    return `${minutes}m ${secs}s`;
  };

  const getStatusColor = (status) => {
    switch (status?.toLowerCase()) {
      case "completed":
        return "text-green-400 bg-[#0C0E12]";
      case "processing":
      case "transcribing":
      case "analyzing":
        return "text-orange-400 bg-[#0C0E12]";
      case "failed":
        return "text-red-400 bg-[#0C0E12]";
      case "flagged":
        return "text-red-400 bg-[#0C0E12]";
      default:
        return "text-blue-400 bg-[#0C0E12]";
    }
  };

  const StatCard = ({ icon, title, value, change }) => (
    <div className="bg-[#0C0E12] rounded-xl p-6 border border-gray-700/50 hover:border-gray-600/50 transition-colors">
      <div className="flex items-center justify-between mb-4">
        <div className="bg-[#7c3aed] p-3 rounded-xl">{icon}</div>
        <div className="flex items-center justify-between">
          <button className="text-gray-400 hover:text-gray-300 p-1">
            <svg width="16" height="16" viewBox="0 0 16 16" fill="currentColor">
              <circle cx="8" cy="3" r="1.5" />
              <circle cx="8" cy="8" r="1.5" />
              <circle cx="8" cy="13" r="1.5" />
            </svg>
          </button>
        </div>
      </div>
      <div className="space-y-2">
        <div className="text-[#94979C] text-[14px] xl:text-[12px] font-medium">
          {title}
        </div>
        <div className="flex items-end justify-between">
          <div className="text-[#F7F7F7] text-[28px] xl:text-[20px] font-bold">
            {value}
          </div>
          <div className="flex items-center text-white text-sm xl:text-[10px] font-medium">
            <TrendUp className="mr-1 text-green-400 xl:w-5 xl:h-5" />
            {change}%
          </div>
        </div>
      </div>
    </div>
  );

  const CallRow = ({ call }) => {
    const tags = call.insights?.tags || [];
    const displayTags = tags.slice(0, 2);
    const remainingTags = tags.length - 2;

    const getTagColor = (index) => {
      const colors = [
        "bg-[#053321] text-white",
        "bg-[#1F235B] text-white",
        "bg-[#2C1C5F] text-white",
      ];
      return colors[index % colors.length];
    };

    const displayStatus = call.insights?.flaggedForReview
      ? "Flagged"
      : call.status;

    return (
      <tr className="border-b border-gray-700 hover:bg-gray-800/50 transition-colors">
        <td className="py-4 px-4 text-white font-medium">
          {call.callerId || call.callerPhone || "Unknown"}
        </td>
        <td className="py-4 px-4 text-gray-400">
          {formatDuration(call.duration)}
        </td>
        <td className="py-4 px-4">
          <span
            className={`inline-flex items-center px-2 py-1 rounded-full border border-[#373A41] text-xs font-medium ${getStatusColor(
              displayStatus
            )}`}
          >
            <div className="w-2 h-2 rounded-full bg-current mr-2"></div>
            {displayStatus?.charAt(0).toUpperCase() + displayStatus?.slice(1)}
          </span>
        </td>
        <td className="py-4 px-4">
          <div className="flex flex-wrap gap-1">
            {displayTags.map((tag, idx) => (
              <span
                key={idx}
                className={`px-3 py-1 rounded-full text-xs font-medium ${getTagColor(
                  idx
                )}`}
              >
                {tag}
              </span>
            ))}
            {remainingTags > 0 && (
              <span className="px-3 py-1 rounded-full text-xs font-medium bg-gray-600 text-white">
                +{remainingTags}
              </span>
            )}
          </div>
        </td>
        <td className="py-4 px-4 text-right">
          <button
            onClick={() => navigate(`/calls/${call.id}`)}
            className="text-gray-400 hover:text-white transition-colors p-1"
          >
            <MoreVertical size={16} />
          </button>
        </td>
      </tr>
    );
  };

  const InsightCard = ({ call }) => {
    const formattedDate = new Date(call.callStartTime).toLocaleDateString(
      "en-US",
      {
        month: "long",
        day: "numeric",
        year: "numeric",
      }
    );

    const tags = call.insights?.tags || [];
    const getTagColor = (index) => {
      const colors = ["bg-blue-600 text-white", "bg-green-600 text-white"];
      return colors[index % colors.length];
    };

    const truncatedSummary = call.insights?.summary
      ? call.insights.summary.length > 200
        ? call.insights.summary.slice(0, 200) + "...."
        : call.insights.summary
      : "No summary available";

    return (
      <div className="bg-[#0C0E12] rounded-lg p-4 sm:p-6 border border-gray-700 mb-4 flex flex-col h-full min-h-[200px] sm:min-h-[250px] lg:min-h-[300px] w-full max-w-full">
        {/* Header Section */}
        <div className="flex items-start justify-between mb-3 sm:mb-4">
          <div className="flex flex-col gap-1 min-w-0 flex-1">
            <div className="flex items-center gap-2">
              <div className="w-2 h-2 bg-purple-500 rounded-full flex-shrink-0"></div>
              <div className="text-white font-semibold text-xs sm:text-sm truncate">
                {call.callerId || call.callerPhone || "Unknown"}
              </div>
            </div>
            <div className="text-gray-400 text-xs flex flex-wrap gap-1">
              <span>{formattedDate}</span>
              <span>•</span>
              <span>{formatDuration(call.duration)}</span>
              <span>•</span>
              <span>ID: {call.id.slice(0, 8)}</span>
            </div>
          </div>
          <button className="text-gray-400 hover:text-white flex-shrink-0 ml-2">
            <MoreVertical size={14} className="sm:w-4 sm:h-4" />
          </button>
        </div>

        {/* Summary Section */}
        <div className="text-gray-300 text-xs sm:text-sm mb-3 sm:mb-4 leading-relaxed flex-grow overflow-hidden">
          {truncatedSummary}
        </div>

        {/* Tags Section */}
        <div className="flex flex-wrap gap-1 sm:gap-2 mb-3 sm:mb-4">
          {tags.slice(0, 3).map((tag, idx) => (
            <span
              key={idx}
              className={`px-2 sm:px-3 py-1 sm:py-1.5 rounded-full text-xs ${getTagColor(
                idx
              )} truncate max-w-[80px] sm:max-w-[120px]`}
            >
              {tag}
            </span>
          ))}
        </div>

        {/* Actions Section */}
        <div className="flex flex-col sm:flex-row gap-2 sm:gap-3 mt-auto">
          <button
            onClick={() => api.exportCalls({ callId: call.id })}
            className="px-4 sm:px-5 py-2 bg-[#0C0E12] border border-[#373A41] text-gray-300 rounded-lg text-xs sm:text-sm hover:bg-gray-600 flex-1 text-center"
          >
            Export
          </button>
          <button
            onClick={() => navigate(`/calls/${call.id}`)}
            className="px-4 sm:px-5 py-2 text-white rounded-lg font-medium text-xs sm:text-sm transition-all duration-300 relative hover:scale-[1.02] hover:shadow-xl flex-1 text-center"
            style={{
              background:
                "radial-gradient(50% 50% at 50% 50%, #744EC5 0%, #5A3D9A 100%)",
              border: "1px solid",
              borderImageSource:
                "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
              boxShadow: `
              0px 36px 32px -16px #7F56D936,
              0px 8px 16px 0px #7F56D91F,
              0px 2px 4px -8px #7F56D914,
              0px 8px 24px -16px #FFFFFF3D inset,
              0px -24px 32px 0px #FFFFFF38 inset,
              0px 0px 12px 0px #FFFFFF inset
            `,
              borderImageSlice: "1",
            }}
          >
            View transcript
          </button>
        </div>
      </div>
    );
  };

  // Handle successful upload
  const handleUploadSuccess = () => {
    setShowUploadModal(false);
    fetchDashboardData();
  };
  // Add function to handle modal close
  const handleCloseModal = () => {
    setShowUploadModal(false);
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#0C0E12] text-white flex items-center justify-center">
        <Loader2 className="animate-spin text-purple-500" size={60} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#0C0E12] text-white">
      <div className="p-8">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4 sm:gap-6 mb-6 sm:mb-8">
          <div className="flex-1 min-w-0">
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold mb-1 sm:mb-2 text-white">
              Welcome {currentUser?.name || "User"}
            </h1>
            <p className="text-sm sm:text-base text-gray-400 leading-relaxed">
              Manage all your audio files, summaries and AI insights.
            </p>
          </div>

          <div className="flex flex-col md:flex-row gap-2 sm:gap-3 shrink-0">
            {/* <button
              onClick={() => navigate("/bulk-import")}
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 bg-[#0C0E12] border border-gray-700 rounded-lg text-gray-300 hover:bg-gray-700 transition-colors text-sm sm:text-base font-medium min-w-0"
            >
              <ImportCurve size={16} className="shrink-0" />
              <span className="whitespace-nowrap">Bulk import</span>
            </button> */}

            <button
              onClick={() => setShowUploadModal(true)}
              className="flex items-center justify-center gap-2 px-3 sm:px-4 py-2 sm:py-2.5 text-white rounded-lg font-medium transition-all duration-300 relative hover:scale-[1.02] hover:shadow-xl text-sm sm:text-base min-w-0"
              style={{
                background:
                  "radial-gradient(50% 50% at 50% 50%, #744EC5 0%, #5A3D9A 100%)",
                border: "1px solid",
                borderImageSource:
                  "radial-gradient(50% 50% at 50% 50%, #FFFFFF 0%, rgba(255, 255, 255, 0) 100%)",
                boxShadow: `
                  0px 36px 32px -16px #7F56D936,
                  0px 8px 16px 0px #7F56D91F,
                  0px 2px 4px -8px #7F56D914,
                  0px 8px 24px -16px #FFFFFF3D inset,
                  0px -24px 32px 0px #FFFFFF38 inset,
                  0px 0px 12px 0px #FFFFFF inset
                `,
                borderImageSlice: "1",
              }}
            >
              <Add size={16} className="shrink-0" />
              <span className="whitespace-nowrap">Upload audio</span>
            </button>
          </div>
        </div>

        {/* Stats Cards */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
          <StatCard
            icon={<SearchNormal />}
            title="Total transcripts"
            value={stats.totalTranscripts}
            change="10"
          />
          <StatCard
            icon={<SearchNormal />}
            title="Calls tagged"
            value={stats.callsTagged}
            change="10"
          />
          <StatCard
            icon={<SearchNormal />}
            title="Summaries generated"
            value={stats.summariesGenerated}
            change="10"
          />
          <StatCard
            icon={<SearchNormal />}
            title="Negative sentiment flags"
            value={stats.negativeSentimentFlags}
            change="10"
          />
        </div>

        {/* Recent Call Recordings */}
        <div className="bg-[#0C0E12] rounded-lg border border-gray-700 mb-8">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between p-6 border-b border-gray-700 gap-4">
            <div>
              <h2 className="text-xl font-bold text-white">
                Recent call recordings
              </h2>
              <p className="text-gray-400 text-sm mt-1">
                Keep track of your audio files.
              </p>
            </div>
            <button
              onClick={() => api.exportCalls()}
              className="flex items-center gap-2 px-4 py-2 bg-[#0C0E12] border-[#373A41] border rounded-lg text-[#CECFD2] hover:bg-gray-600 transition-colors self-start sm:self-auto"
            >
              <ExportCurve size={16} />
              Export
            </button>
          </div>

          <div className="overflow-x-auto">
            {recentCalls.length === 0 ? (
              <div className="text-center py-20 text-gray-400">
                No call recordings found
              </div>
            ) : (
              <table className="w-full min-w-[800px]">
                <thead className="bg-[#13161B]">
                  <tr className="border-b border-[#22262F]">
                    <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">
                      Caller ID
                    </th>
                    <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">
                      Duration
                    </th>
                    <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">
                      Status
                    </th>
                    <th className="text-left py-4 px-4 text-gray-400 font-medium text-sm">
                      Tags
                    </th>
                    <th className="text-right py-4 px-4 text-gray-400 font-medium text-sm"></th>
                  </tr>
                </thead>
                <tbody>
                  {recentCalls.map((call) => (
                    <CallRow key={call.id} call={call} />
                  ))}
                </tbody>
              </table>
            )}
          </div>

          <div className="flex flex-col sm:flex-row sm:items-center justify-between p-6 border-t border-gray-700 gap-4">
            <div className="flex gap-2">
              <button
                onClick={() => navigate("/transcripts")}
                className="px-3 py-1 bg-[#0C0E12] border-[#373A41] border text-gray-300 rounded hover:bg-gray-600 transition-colors"
              >
                View All
              </button>
            </div>
            <span className="text-gray-400 text-sm">
              Showing {recentCalls.length} most recent
            </span>
          </div>
        </div>

        {/* AI Insights */}
        <div className="mb-8">
          <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4 lg:gap-6 mb-8">
            <div>
              <h2 className="text-xl md:text-2xl font-bold text-white">
                AI Insights
              </h2>
              <p className="text-gray-400 text-sm mt-1">
                Keep track of your audio files.
              </p>
            </div>

            <div className="flex flex-col sm:flex-row gap-3 lg:gap-2">
              <div className="flex flex-wrap gap-1">
                {["12 months", "30 days", "7 days", "24 hours"].map(
                  (period) => (
                    <button
                      key={period}
                      onClick={() => setActiveFilter(period)}
                      className={`px-3 py-2 rounded-md text-sm font-medium transition-colors border ${
                        activeFilter === period
                          ? "bg-[#0C0E12] text-white border-gray-600"
                          : "bg-[#0C0E12] text-gray-300 border-gray-700 hover:bg-gray-700 hover:text-white"
                      }`}
                    >
                      {period}
                    </button>
                  )
                )}
              </div>

              <div className="flex gap-2">
                <button className="flex items-center gap-2 px-3 py-2 bg-[#0C0E12] text-gray-300 rounded-md text-sm font-medium hover:bg-gray-700 hover:text-white transition-colors border border-gray-700">
                  <Sort size={14} />
                  <span className="hidden sm:inline">Filter</span>
                </button>
                <button
                  onClick={() => api.exportCalls()}
                  className="flex items-center gap-2 px-3 py-2 bg-[#0C0E12] text-gray-300 rounded-md text-sm font-medium hover:bg-gray-700 hover:text-white transition-colors border border-gray-700"
                >
                  <ExportCurve size={14} />
                  <span className="hidden sm:inline">Export</span>
                </button>
              </div>
            </div>
          </div>

          <div className="flex gap-6 overflow-x-auto pb-4 horizontal-scroll-container">
            {recentCalls
              .filter((call) => call.insights?.summary)
              .slice(0, 5)
              .map((call) => (
                <div key={call.id} className="flex-shrink-0 w-[500px]">
                  <InsightCard call={call} />
                </div>
              ))}
            {recentCalls.filter((call) => call.insights?.summary).length ===
              0 && (
              <div className="text-center py-20 text-gray-400 w-full">
                No AI insights available yet
              </div>
            )}
          </div>
        </div>

        {/* AI Trends */}
        <div className="flex flex-col sm:flex-row items-start sm:items-center justify-between mb-6">
          <div className="mb-4 sm:mb-0">
            <h2 className="text-xl font-semibold">AI trends</h2>
            <p className="text-gray-400 text-sm mt-1">
              Understand customer interactions and voice data performance.
            </p>
          </div>
          <div className="flex gap-3">
            <button className="flex items-center gap-2 px-4 py-2 border border-gray-700 text-gray-300 rounded-md text-sm hover:bg-gray-800 transition-colors">
              <Filter size={16} />
              Filter
            </button>
            <button
              onClick={() => api.exportCalls()}
              className="flex items-center gap-2 px-4 py-2 border border-gray-700 text-gray-300 rounded-md text-sm hover:bg-gray-800 transition-colors"
            >
              <Download size={16} />
              Export
            </button>
          </div>
        </div>

        {/* Charts Grid */}
        <div className="bg-[#13131A] rounded-lg border border-gray-800 p-6 mb-8">
          <div className="flex items-center justify-between mb-6">
            <div>
              <h3 className="text-base font-medium">
                Transcripts volume over time
              </h3>
              <p className="text-gray-500 text-sm mt-1">Sentiment trend</p>
            </div>
            <button className="text-gray-500 hover:text-gray-300 transition-colors">
              <MoreVertical size={20} />
            </button>
          </div>
          <div className="h-64">
            <ResponsiveContainer width="100%" height="100%">
              <AreaChart
                data={chartData.volumeData}
                margin={{ top: 5, right: 5, left: 5, bottom: 5 }}
              >
                <defs>
                  <linearGradient
                    id="volumeGradient"
                    x1="0"
                    y1="0"
                    x2="0"
                    y2="1"
                  >
                    <stop offset="5%" stopColor="#8B5CF6" stopOpacity={0.3} />
                    <stop offset="95%" stopColor="#8B5CF6" stopOpacity={0} />
                  </linearGradient>
                </defs>
                <CartesianGrid
                  strokeDasharray="3 3"
                  stroke="#374151"
                  vertical={false}
                />
                <XAxis
                  dataKey="date"
                  stroke="#9CA3AF"
                  fontSize={12}
                  axisLine={false}
                  tickLine={false}
                />
                <YAxis
                  stroke="#9CA3AF"
                  fontSize={12}
                  axisLine={false}
                  tickLine={false}
                />
                <Tooltip content={<CustomTooltip />} />
                <Area
                  type="monotone"
                  dataKey="transcripts"
                  stroke="#8B5CF6"
                  strokeWidth={3}
                  fill="url(#volumeGradient)"
                  dot={{ fill: "#8B5CF6", strokeWidth: 2, r: 4 }}
                  activeDot={{ r: 6, fill: "#8B5CF6" }}
                />
              </AreaChart>
            </ResponsiveContainer>
          </div>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          {/* Sentiment Trend */}
          <div className="bg-[#13131A] rounded-xl border border-gray-800 p-6 transition-all duration-300 hover:shadow-2xl">
            <div className="flex items-center justify-between mb-6">
              <div className="space-y-1">
                <h3 className="text-lg font-semibold text-white bg-gradient-to-r from-white to-gray-300 bg-clip-text text-transparent">
                  Sentiment Trend
                </h3>
                <p className="text-gray-400 text-sm">
                  Customer sentiment over time
                </p>
              </div>
              <button className="text-gray-500 hover:text-white transition-all duration-300 hover:bg-gray-800 p-2 rounded-lg">
                <MoreVertical size={20} />
              </button>
            </div>

            {/* Legend with improved styling */}
            <div className="flex flex-wrap items-center gap-4 mb-6">
              <div className="flex items-center gap-2 group cursor-pointer">
                <div className="w-3 h-3 bg-green-400 rounded-full group-hover:scale-125 transition-transform duration-300 shadow-lg shadow-green-400/30"></div>
                <span className="text-gray-300 text-sm font-medium group-hover:text-green-400 transition-colors">
                  Positive
                </span>
              </div>
              <div className="flex items-center gap-2 group cursor-pointer">
                <div className="w-3 h-3 bg-purple-500 rounded-full group-hover:scale-125 transition-transform duration-300 shadow-lg shadow-purple-500/30"></div>
                <span className="text-gray-300 text-sm font-medium group-hover:text-purple-400 transition-colors">
                  Negative
                </span>
              </div>
              <div className="flex items-center gap-2 group cursor-pointer">
                <div className="w-3 h-3 bg-gray-400 rounded-full group-hover:scale-125 transition-transform duration-300 shadow-lg shadow-gray-400/30"></div>
                <span className="text-gray-300 text-sm font-medium group-hover:text-gray-200 transition-colors">
                  Neutral
                </span>
              </div>
            </div>

            {/* Chart Container with enhanced styling */}
            <div className="h-64 relative group">
              {/* Animated background gradient */}
              <div className="absolute inset-0 bg-gradient-to-br from-purple-500/5 to-transparent rounded-lg opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>

              <ResponsiveContainer width="100%" height="100%">
                <BarChart
                  data={sentimentData}
                  margin={{ top: 20, right: 20, left: 0, bottom: 10 }}
                >
                  <defs>
                    {/* Gradient for positive bars */}
                    <linearGradient
                      id="positiveGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor="#10B981" stopOpacity={0.9} />
                      <stop
                        offset="100%"
                        stopColor="#10B981"
                        stopOpacity={0.4}
                      />
                    </linearGradient>

                    {/* Gradient for negative bars */}
                    <linearGradient
                      id="negativeGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor="#8B5CF6" stopOpacity={0.9} />
                      <stop
                        offset="100%"
                        stopColor="#8B5CF6"
                        stopOpacity={0.4}
                      />
                    </linearGradient>

                    {/* Gradient for neutral bars */}
                    <linearGradient
                      id="neutralGradient"
                      x1="0"
                      y1="0"
                      x2="0"
                      y2="1"
                    >
                      <stop offset="0%" stopColor="#6B7280" stopOpacity={0.9} />
                      <stop
                        offset="100%"
                        stopColor="#6B7280"
                        stopOpacity={0.4}
                      />
                    </linearGradient>
                  </defs>

                  <CartesianGrid
                    strokeDasharray="3 3"
                    stroke="#374151"
                    vertical={false}
                    opacity={0.5}
                  />

                  <XAxis
                    dataKey="day"
                    axisLine={false}
                    tickLine={false}
                    tick={{
                      fill: "#9CA3AF",
                      fontSize: 11,
                      fontWeight: 500,
                    }}
                    padding={{ left: 10, right: 10 }}
                  />

                  <YAxis
                    axisLine={false}
                    tickLine={false}
                    tick={{
                      fill: "#9CA3AF",
                      fontSize: 11,
                      fontWeight: 500,
                    }}
                    domain={[0, "dataMax + 5"]}
                  />

                  <Tooltip
                    content={({ active, payload, label }) => {
                      if (active && payload && payload.length) {
                        return (
                          <div className="bg-[#1A1D23] border border-gray-600 rounded-lg p-3 shadow-2xl backdrop-blur-sm animate-in fade-in-0 zoom-in-95">
                            <p className="text-white font-semibold text-sm mb-2">
                              {label}
                            </p>
                            <div className="space-y-1">
                              {payload.map((entry, index) => (
                                <div
                                  key={index}
                                  className="flex items-center justify-between gap-4"
                                >
                                  <div className="flex items-center gap-2">
                                    <div
                                      className="w-2 h-2 rounded-full"
                                      style={{ backgroundColor: entry.color }}
                                    ></div>
                                    <span className="text-gray-300 text-xs capitalize">
                                      {entry.dataKey}
                                    </span>
                                  </div>
                                  <span className="text-white font-medium text-xs">
                                    {entry.value}
                                  </span>
                                </div>
                              ))}
                            </div>
                          </div>
                        );
                      }
                      return null;
                    }}
                    cursor={{
                      fill: "rgba(139, 92, 246, 0.1)",
                      stroke: "rgba(139, 92, 246, 0.3)",
                      strokeWidth: 1,
                    }}
                  />

                  {/* Animated Bars */}
                  <Bar
                    dataKey="positive"
                    stackId="a"
                    fill="url(#positiveGradient)"
                    radius={[2, 2, 0, 0]}
                    className="hover:shadow-lg hover:shadow-green-400/20 transition-all duration-300"
                  >
                    {sentimentData.map((entry, index) => (
                      <Cell
                        key={`positive-${index}`}
                        className="hover:opacity-80 transition-opacity duration-200"
                      />
                    ))}
                  </Bar>

                  <Bar
                    dataKey="negative"
                    stackId="a"
                    fill="url(#negativeGradient)"
                    radius={[2, 2, 0, 0]}
                    className="hover:shadow-lg hover:shadow-purple-500/20 transition-all duration-300"
                  >
                    {sentimentData.map((entry, index) => (
                      <Cell
                        key={`negative-${index}`}
                        className="hover:opacity-80 transition-opacity duration-200"
                      />
                    ))}
                  </Bar>

                  <Bar
                    dataKey="neutral"
                    stackId="a"
                    fill="url(#neutralGradient)"
                    radius={[2, 2, 0, 0]}
                    className="hover:shadow-lg hover:shadow-gray-400/20 transition-all duration-300"
                  >
                    {sentimentData.map((entry, index) => (
                      <Cell
                        key={`neutral-${index}`}
                        className="hover:opacity-80 transition-opacity duration-200"
                      />
                    ))}
                  </Bar>
                </BarChart>
              </ResponsiveContainer>

              {/* Loading shimmer effect */}
              {sentimentData.length === 0 && (
                <div className="absolute inset-0 flex items-center justify-center bg-[#13131A]/80 backdrop-blur-sm rounded-lg">
                  <div className="flex items-center gap-3 text-gray-400">
                    <div className="animate-spin rounded-full h-6 w-6 border-2 border-purple-500 border-t-transparent"></div>
                    <span className="text-sm">Loading sentiment data...</span>
                  </div>
                </div>
              )}
            </div>

            {/* Summary Stats */}
            {sentimentData.length > 0 && (
              <div className="mt-4 pt-4 border-t border-gray-800">
                <div className="grid grid-cols-3 gap-4 text-center">
                  <div className="group cursor-pointer">
                    <div className="text-green-400 text-lg font-bold group-hover:scale-110 transition-transform duration-300">
                      {sentimentData.reduce(
                        (sum, day) => sum + (day.positive || 0),
                        0
                      )}
                    </div>
                    <div className="text-gray-400 text-xs mt-1">
                      Total Positive
                    </div>
                  </div>
                  <div className="group cursor-pointer">
                    <div className="text-purple-400 text-lg font-bold group-hover:scale-110 transition-transform duration-300">
                      {sentimentData.reduce(
                        (sum, day) => sum + (day.negative || 0),
                        0
                      )}
                    </div>
                    <div className="text-gray-400 text-xs mt-1">
                      Total Negative
                    </div>
                  </div>
                  <div className="group cursor-pointer">
                    <div className="text-gray-300 text-lg font-bold group-hover:scale-110 transition-transform duration-300">
                      {sentimentData.reduce(
                        (sum, day) => sum + (day.neutral || 0),
                        0
                      )}
                    </div>
                    <div className="text-gray-400 text-xs mt-1">
                      Total Neutral
                    </div>
                  </div>
                </div>
              </div>
            )}
          </div>

          {/* Tag Category Share */}
          <div className="bg-[#13131A] rounded-lg border border-gray-800 p-6 h-fit">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-base font-medium">Tag Category Share</h3>
              <button className="text-gray-500 hover:text-gray-300 transition-colors">
                <MoreVertical size={20} />
              </button>
            </div>
            <div className="flex flex-col items-center">
              {pieData.length > 0 ? (
                <>
                  <div className="w-64 h-64 relative">
                    <ResponsiveContainer width="100%" height="100%">
                      <PieChart>
                        <Pie
                          data={pieData}
                          cx="50%"
                          cy="50%"
                          innerRadius={60}
                          outerRadius={100}
                          paddingAngle={2}
                          dataKey="value"
                        >
                          {pieData.map((entry, index) => (
                            <Cell key={`cell-${index}`} fill={entry.color} />
                          ))}
                        </Pie>
                      </PieChart>
                    </ResponsiveContainer>
                  </div>
                  <div className="grid grid-cols-2 gap-x-8 gap-y-3 mt-6 mb-8">
                    {pieData.map((item, index) => (
                      <div
                        key={index}
                        className="flex items-center gap-2 text-sm"
                      >
                        <div
                          className="w-3 h-3 rounded-full flex-shrink-0"
                          style={{ backgroundColor: item.color }}
                        ></div>
                        <span className="text-gray-300">{item.name}</span>
                      </div>
                    ))}
                  </div>
                </>
              ) : (
                <div className="text-gray-400 py-10">No tag data available</div>
              )}
              <button
                onClick={() => navigate("/insights")}
                className="w-full px-4 py-3 bg-[#8B5CF6] text-white rounded-lg hover:bg-[#7C3AED] transition-colors font-medium"
              >
                View full report
              </button>
            </div>
          </div>
        </div>
      </div>

      {/* Upload Audio Modal */}
      {showUploadModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/80 backdrop-blur-sm">
          <div className="relative bg-[#0C0E12] border border-gray-700 rounded-xl w-full max-w-4xl max-h-[90vh] overflow-y-auto">
            {/* Modal Header */}
            <div className="sticky top-0 bg-[#0C0E12] border-b border-gray-700 p-6 z-10">
              <div className="flex items-center justify-between">
                <div>
                  <h2 className="text-2xl font-bold text-white">
                    Upload Call Recording
                  </h2>
                  <p className="text-gray-400 mt-1">
                    Upload audio files for transcription and analysis.
                  </p>
                </div>
                <button
                  onClick={handleCloseModal}
                  className="text-gray-400 hover:text-white p-2 transition-colors"
                >
                  <X size={24} />
                </button>
              </div>
            </div>

            {/* Modal Content */}
            <div className="">
              <UploadCall
                onSuccess={handleUploadSuccess}
                onCancel={handleCloseModal}
                isModal={true}
              />
            </div>
          </div>
        </div>
      )}

      <AskGenziButton />
    </div>
  );
};

export default Dashboard;
